import { Dialog, Text, COLORS, SayIcon } from "@asayinc/component-library";
import { Box, Divider, Stack, Chip, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import dayjs from "dayjs";

import { IAdditionalMailingRequest } from "src/store/additionalMailingRequest/types";
import {
  ADDITIONAL_MAILING_TYPE_LABEL,
  ADDITIONAL_MAILING_AUDIENCE_LABEL,
} from "src/constants/labels/additionalMailingLabel";
import { ADDITIONAL_MAILING_REQUEST_STATUS_LABEL } from "src/constants/labels/additionalMailingRequestLabel";

interface AdditionalMailingRequestDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  request: IAdditionalMailingRequest | null;
  onEdit: (request: IAdditionalMailingRequest) => void;
  onDelete: (request: IAdditionalMailingRequest) => void;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", py: 1.5 }}>
      <Text
        variant="body3"
        sx={{ width: 180, flexShrink: 0, color: COLORS.P_MED_LOW_EMPHASIS }}
      >
        {label}
      </Text>
      <Text variant="body3">{value}</Text>
    </Box>
  );
}

export function AdditionalMailingRequestDetailsDialog({
  open,
  onClose,
  request,
  onEdit,
  onDelete,
}: AdditionalMailingRequestDetailsDialogProps) {
  if (!request) return null;

  const isPending = request.operationsStatus === "pending";
  const statusColor =
    request.operationsStatus === "approved"
      ? "success"
      : request.operationsStatus === "rejected"
      ? "error"
      : "default";

  const buttonActions = [
    {
      label: "Edit",
      onClick: () => onEdit(request),
      variant: "primary" as const,
      size: "small" as const,
      disabled: !isPending,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Additional Mailing Request Details"
      content={
        <Box sx={{ position: "relative" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: -48, right: -16 }}
          >
            {isPending && (
              <IconButton onClick={() => onDelete(request)} size="small">
                <SayIcon
                  name="TrashCan"
                  size="20px"
                  color={COLORS.ERROR_DARK}
                />
              </IconButton>
            )}
            <IconButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          <Stack spacing={2}>
            <Box>
              <Chip
                label={
                  ADDITIONAL_MAILING_REQUEST_STATUS_LABEL[
                    request.operationsStatus
                  ] || request.operationsStatus
                }
                color={statusColor}
                size="small"
              />
            </Box>

            <Divider />

            <DetailRow
              label="Type"
              value={
                ADDITIONAL_MAILING_TYPE_LABEL[request.type] || request.type
              }
            />
            <DetailRow
              label="Audience"
              value={
                ADDITIONAL_MAILING_AUDIENCE_LABEL[request.audience] ||
                request.audience
              }
            />
            <DetailRow
              label="Send Date"
              value={
                request.sendDate
                  ? dayjs(request.sendDate).format("MMMM DD, YYYY")
                  : "-"
              }
            />
            <DetailRow
              label="Minimum Shares"
              value={request.minimumShares?.toLocaleString() || "0"}
            />

            <DetailRow
              label="Email Notifications"
              value={request.sendEmailNotifications ? "Yes" : "No"}
            />
            <DetailRow
              label="In-App Notifications"
              value={request.sendInAppNotifications ? "Yes" : "No"}
            />

            {request.rejectionReason && (
              <>
                <Divider />
                <DetailRow
                  label="Rejection Reason"
                  value={request.rejectionReason}
                />
              </>
            )}

            <DetailRow
              label="Created"
              value={
                request.created
                  ? dayjs(request.created).format("MMMM DD, YYYY h:mm A")
                  : "-"
              }
            />
            {request.reviewedAt && (
              <DetailRow
                label="Reviewed At"
                value={dayjs(request.reviewedAt).format("MMMM DD, YYYY h:mm A")}
              />
            )}
          </Stack>
        </Box>
      }
      buttonActions={buttonActions}
      ctaOrientation="horizontal"
    />
  );
}
